import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { autoSavePDF } from '../slice/PDFEditSlice';

/**
 * Debounced "export the open PDF and upload it" autosave for the document
 * viewer.
 *
 * Background (Matthew, 2026-07-27): `TabView.handleAutoSave` used to call
 * `showAutoSaveIndicator` and nothing else — the `autoSavePDF` import was
 * commented out — so every field edit flashed "Saved to <name>" while the
 * file was never uploaded. Two production documents were verified typed-into
 * and never saved: field audit rows existed for the typed values, but
 * `dms_meta.last_modified` still matched `uploaded_at` to the microsecond.
 * Nothing may report success unless the upload resolved.
 *
 * Timing: a save exports the whole PDF and re-uploads it, so edits are
 * coalesced. `DEBOUNCE_MS` covers a typing pause; `MAX_WAIT_MS` bounds how
 * long continuous typing can defer a save.
 */
const DEBOUNCE_MS = 1200;
const MAX_WAIT_MS = 8000;

const useDocumentAutoSave = ({ onSaveStart, onSaveSuccess, onSaveError } = {}) => {
  const dispatch = useDispatch();

  // Latest requested save; replaced (not queued) so only the newest document
  // state is ever uploaded.
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  // Absolute time by which a save must run even if edits keep arriving.
  const deadlineRef = useRef(0);
  const inFlightRef = useRef(false);
  const uploadRef = useRef(null);
  const runRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const run = useCallback(async () => {
    clearTimer();
    deadlineRef.current = 0;

    const job = pendingRef.current;
    if (!job) return;

    // An upload is already running; leave the job pending so the `finally`
    // below picks it up. Uploads never overlap for the same document.
    if (inFlightRef.current) return;

    const { documentViewer, annotationManager, documentId, documentName } = job;
    const doc = documentViewer?.getDocument?.();
    if (!doc || !annotationManager || !documentId) {
      pendingRef.current = null;
      return;
    }

    pendingRef.current = null;
    inFlightRef.current = true;
    const upload = (async () => {
      try {
        onSaveStart?.(documentName);
        const xfdfString = await annotationManager.exportAnnotations();
        const data = await doc.getFileData({ xfdfString, doctype: 'pdf' });
        const file = new Blob([data], { type: 'application/pdf' });
        const response = await dispatch(autoSavePDF({ id: documentId, file })).unwrap();
        onSaveSuccess?.(documentName, response);
      } catch (error) {
        onSaveError?.(error, documentName);
      }
    })();
    uploadRef.current = upload;
    try {
      await upload;
    } finally {
      inFlightRef.current = false;
      uploadRef.current = null;
      if (pendingRef.current) runRef.current?.();
    }
  }, [dispatch, onSaveStart, onSaveSuccess, onSaveError]);

  runRef.current = run;

  const scheduleAutoSave = useCallback((job) => {
    if (!job?.documentId) return;
    pendingRef.current = job;

    const now = Date.now();
    if (!deadlineRef.current) deadlineRef.current = now + MAX_WAIT_MS;
    clearTimer();
    const wait = Math.max(0, Math.min(DEBOUNCE_MS, deadlineRef.current - now));
    timerRef.current = setTimeout(() => runRef.current?.(), wait);
  }, []);

  /** Run any pending save immediately (tab hidden, document closing, unmount). */
  const flushAutoSave = useCallback(() => {
    if (pendingRef.current) runRef.current?.();
  }, []);

  /** Run any pending save now and resolve once no upload is left running. */
  const settleAutoSave = useCallback(async () => {
    for (;;) {
      if (pendingRef.current && !inFlightRef.current) runRef.current?.();
      const upload = uploadRef.current;
      if (!upload) return;
      await upload;
      // Let the finished run's cleanup start any save queued behind it.
      await Promise.resolve();
    }
  }, []);

  /** Drop a pending save without running it. */
  const cancelAutoSave = useCallback(() => {
    clearTimer();
    deadlineRef.current = 0;
    pendingRef.current = null;
  }, []);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushAutoSave();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('beforeunload', flushAutoSave);
    window.addEventListener('pagehide', flushAutoSave);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('beforeunload', flushAutoSave);
      window.removeEventListener('pagehide', flushAutoSave);
      clearTimer();
      flushAutoSave();
    };
  }, [flushAutoSave]);

  return { scheduleAutoSave, flushAutoSave, settleAutoSave, cancelAutoSave };
};

export default useDocumentAutoSave;
